import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, TablePagination } from '@mui/material';
import { ReloadIconButton } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS } from '../../constants/constants';
import { Card, CardContent, CardHeader } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import { emptyFarmsOverviewQuery, farmRowKey, FarmsOverviewQuery, hasActiveFilters } from '../../models/farms-overview';
import useFarmsOverview from '../../hooks/useFarmsOverview';
import { pigletRegistrationColors as c } from '../PigletRegistration/colors';
import { deviceSyncColors } from '../DeviceSyncStatus/colors';
import ReadersStatusWidget from '../ReadersStatusWidget';
import FarmsOverviewKpis from './FarmsOverviewKpis';
import FarmsOverviewFilters from './FarmsOverviewFilters';
import FarmQueueRow from './FarmQueueRow';

/** Rows arrive one after another, but only the first few are staggered - a long page should not trickle in. */
const ROW_STAGGER_MS = 30;
const ROW_STAGGER_MAX = 8;

/** Typing is not a request. Every other part of the query refetches immediately; only the box waits. */
const SEARCH_DEBOUNCE_MS = 350;

const useDebounced = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

/** Legend for the sparkline column, hand-rolled so the expected line shows as a dash and the running cycle hatched. */
const QueueLegend = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.registered }} />
        {t('f-farms-overview-legend-cycle')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.belowExpected }} />
        {t('f-piglets-reg-legend-below-cycle')}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 rounded-[2px]"
          style={{
            background: `repeating-linear-gradient(45deg, ${c.inProgress.stripe} 0 2px, ${c.inProgress.ground} 2px 4px)`
          }}
        />
        {t('f-piglets-reg-legend-in-progress')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-4 border-t-2 border-dashed" style={{ borderColor: c.expected }} />
        {t('f-farms-overview-legend-expected')}
      </span>
    </div>
  );
};

/**
 * Says how many farms have a device sitting on unsent animals, and is the switch that narrows the list to
 * them. A per-row badge alone is not enough: the list is paged, so the one farm with a stuck device can be on
 * a page nobody opens.
 *
 * Stays up while the filter is on even once the count reaches zero, because it is then the only way back out.
 */
const PendingSyncNotice = ({ count, active, onToggle }: { count: number; active: boolean; onToggle: () => void }) => {
  const { t } = useTranslation();

  if (count <= 0 && !active) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: deviceSyncColors.delayed }} />
      <span className="mr-auto">
        {count > 0 ? t('f-farms-overview-unsynced-notice', { count }) : t('f-farms-overview-unsynced-notice-none')}
      </span>
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex h-8 cursor-pointer appearance-none items-center rounded-md border border-amber-300 bg-white px-3 text-sm font-medium text-amber-900 shadow-sm transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        {t(active ? 'f-farms-overview-unsynced-show-all' : 'f-farms-overview-unsynced-show')}
      </button>
    </div>
  );
};

const QueueSkeleton = () => (
  <div className="divide-y divide-gray-100">
    {[0, 1, 2, 3, 4].map((row) => (
      <div key={row} className="flex items-center gap-4 px-3 py-4">
        <div className="w-64 shrink-0">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-2 h-3 w-52" />
        </div>
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-9 w-36 shrink-0" />
        <Skeleton className="h-9 w-28 shrink-0" />
      </div>
    ))}
  </div>
);

/**
 * Admin farms overview.
 *
 * Every farm is on the list, each carrying the badge that says where it stands - a farm doing fine is
 * visibly fine rather than absent, which is the difference between "all clear" and "the request failed".
 * Worst first is still the API's default order, and the status filter is how a large estate narrows to the
 * farms that need a call.
 *
 * The list opens on most registered - see `emptyFarmsOverviewQuery` - and the order is the reader's to
 * change from there.
 */
const FarmsOverview: React.FC = () => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState<FarmsOverviewQuery>(emptyFarmsOverviewQuery);

  // The box keeps the raw text so typing stays responsive; the request follows a beat later.
  const debouncedSearch = useDebounced(query.searchText, SEARCH_DEBOUNCE_MS);
  const { overview, isBusy, loaded, reload } = useFarmsOverview({ ...query, searchText: debouncedSearch });

  const farms = overview?.data ?? [];
  const hasFilters = hasActiveFilters(query);

  const renderQueue = () => {
    if (isBusy || !overview) return <QueueSkeleton />;

    if (farms.length === 0) {
      return (
        <div className="px-3 py-10 text-center text-sm text-gray-500">
          {/* Unfiltered, an empty list now means there are no farms at all - every farm is listed, healthy
              ones included, so "all clear" is something the badges say, not something an empty screen does. */}
          {t(hasFilters ? 'f-farms-overview-empty-filtered' : 'f-farms-overview-empty-no-farms')}
        </div>
      );
    }

    // Every fetch goes through the skeleton, so the rows remount and replay this on each filter, sort or page
    // change - which is the point: it shows the list was actually replaced.
    return (
      <div className="divide-y divide-gray-100">
        {farms.map((farm, index) => (
          <div
            key={farmRowKey(farm)}
            className="motion-safe:animate-hs-rise"
            style={{ animationDelay: `${Math.min(index, ROW_STAGGER_MAX) * ROW_STAGGER_MS}ms` }}
          >
            <FarmQueueRow
              farm={farm}
              lookbackWeeks={overview.lookbackWeeks}
              birthWeightTolerancePercent={overview.unmeasuredBirthWeightPercent ?? null}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ maxWidth: 1920 }}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t('f-farms-overview-title')}</h1>
        </div>

        <ReloadIconButton action={reload} />
      </div>

      <div className="mb-6">
        <FarmsOverviewKpis overview={overview} isBusy={isBusy} />
      </div>

      <div className="mb-4">
        <FarmsOverviewFilters value={query} onChange={setQuery} />
      </div>

      {overview && (
        <div className="mb-4 grid gap-2 empty:hidden">
          <PendingSyncNotice
            count={overview.summary.farmsWithPendingAnimals ?? 0}
            active={query.pendingOnly}
            onToggle={() => setQuery((current) => ({ ...current, pendingOnly: !current.pendingOnly, page: 0 }))}
          />
        </div>
      )}

      <Card>
        <CardHeader className="space-y-1 pb-4">
          <h2 className="text-base font-semibold text-gray-900">{t('f-farms-overview-queue-title')}</h2>
          <p className="text-xs text-gray-500">
            {t('f-farms-overview-queue-subtitle-cycles', {
              lookback: overview?.lookbackWeeks ?? 0,
              sparkline: overview?.sparklineWeeks ?? 0
            })}
          </p>
          <div className="pt-2">
            <QueueLegend />
          </div>
        </CardHeader>

        <CardContent className="px-3 pb-3">{renderQueue()}</CardContent>

        {/* Paging is server-side, so it stays mounted while a page loads rather than disappearing under the
            skeleton and dropping the reader back to the top of the list. */}
        {loaded && overview && overview.total > 0 && (
          <div className="border-t border-gray-100">
            <TablePagination
              component="div"
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={overview.total}
              rowsPerPage={query.size}
              page={query.page}
              onPageChange={(_event, page) => setQuery((current) => ({ ...current, page }))}
              onRowsPerPageChange={(event) =>
                setQuery((current) => ({ ...current, size: parseInt(event.target.value, 10), page: 0 }))
              }
            />
          </div>
        )}
      </Card>

      {/* Hardware health sits below the queue, and under its own paging, because it is a different job: the
          queue is farms to call today, this is devices to fix this week. It loads independently, so a slow or
          failed device call never holds up the queue above it. */}
      <div className="mt-6">
        <ReadersStatusWidget />
      </div>
    </Container>
  );
};

export default FarmsOverview;
